import Image from "next/image";

import { useTranslation } from "@flare/i18n";
import { Heading, Text } from "@flare/ui/components";

import fiveStars from "@/../public/next/images/five-stars-rating.png";

import { ActionButton } from "../components/action-button";
import { ActionsContainer } from "../components/actions-container";
import { BackButton } from "../components/back-button";
import { Footer } from "../components/footer";
import { StepContainer } from "../components/step-container";
import { StepContent } from "../components/step-content";

import type { StepProps } from "./step.types";

const attorneyNames = {
  arizona: "Lowen Jones",
  california: "Michael Gower",
  colorado: "Tawni Cummings",
  florida: "Vanessa Tuttle",
  georgia: "Kellyn Kidwell",
  illinois: "Walter Jackowiec",
  maryland: "Diane Goodman",
  michigan: "Anna Tsaturova",
  "new-york": "Jennifer Paulino",
  texas: "Julie Gray",
};

export default function LocationConfirmation({ answers }: StepProps) {
  const { state } = answers;
  const { t } = useTranslation();

  const formattedState = state
    ?.toLowerCase()
    .replace(" ", "-") as keyof typeof attorneyNames;

  const capitalizedState = state && state[0].toUpperCase() + state?.slice(1);

  const title = state
    ? t("FUNNEL_LOCATION_CONFIRMATION_DYNAMIC_TITLE", {
        state: capitalizedState,
      })
    : t("FUNNEL_LOCATION_CONFIRMATION_FALLBACK_TITLE");

  const imageMobile = (
    <Image
      src={
        state
          ? `/next/images/attorneys/${formattedState}-attorney-mobile.jpeg`
          : "/next/images/attorneys/attorneys-mobile.jpeg"
      }
      alt={
        state
          ? t("FUNNEL_LOCATION_CONFIRMATION_DYNAMIC_IMAGE_ALT", {
              state,
            })
          : t("FUNNEL_LOCATION_CONFIRMATION_FALLBACK_IMAGE_ALT")
      }
      layout="responsive"
      width={503}
      height={420}
      className="rounded-lg md:hidden"
    />
  );

  const imageDesktop = (
    <Image
      src={
        state
          ? `/next/images/attorneys/${formattedState}-attorney-desktop.jpeg`
          : "/next/images/attorneys/attorneys-desktop.jpeg"
      }
      alt={
        state
          ? t("FUNNEL_LOCATION_CONFIRMATION_DYNAMIC_IMAGE_ALT", {
              state,
            })
          : t("FUNNEL_LOCATION_CONFIRMATION_FALLBACK_IMAGE_ALT")
      }
      layout="responsive"
      width={485}
      height={458}
      className="hidden rounded-lg md:block"
    />
  );

  return (
    <>
      <StepContainer>
        <StepContent>
          <BackButton />
          <div className="m-auto flex w-full max-w-[1010px] flex-col items-center justify-between gap-sp-7 md:flex-row md:pt-sp-9">
            <div className="flex w-full flex-col items-center md:block md:w-1/2">
              <Heading
                variant="h3"
                textWrap="balance"
                className="text-center md:text-left"
              >
                {title}
              </Heading>
              <Text className="mt-sp-3 max-w-[450px] text-center md:my-sp-6 md:text-left">
                {t("FUNNEL_LOCATION_CONFIRMATION_SUBTITLE")}
              </Text>
            </div>
            <div className="relative w-full rounded-xl md:w-1/2">
              {imageMobile}
              {imageDesktop}
              {state && (
                <>
                  <div className="absolute top-sp-4 flex w-full items-center justify-between gap-sp-5 px-sp-4 md:top-sp-5 md:px-sp-5">
                    <div className="flex items-center gap-sp-3">
                      <Image
                        src="/next/images/verified-icon.svg"
                        alt={t("VERIFIED")}
                        width={16}
                        height={16}
                      />
                      <Text
                        variant="body2-highlight"
                        textColor="primary-inverted"
                      >
                        {t("VERIFIED")}
                      </Text>
                    </div>
                    <Image
                      src={fiveStars}
                      alt={t("RATING")}
                      className="w-[90px] md:w-[120px]"
                    />
                  </div>
                  <div className="absolute bottom-sp-4 left-sp-4 md:bottom-sp-5 md:left-sp-5">
                    <Heading variant="h3" textColor="primary-inverted">
                      {attorneyNames[formattedState]}
                    </Heading>
                    <Text variant="caption" textColor="primary-inverted">
                      {t(
                        "FUNNEL_LOCATION_CONFIRMATION_IMAGE_MANAGING_ATTORNEY",
                        {
                          state: capitalizedState,
                        },
                      )}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </div>
        </StepContent>
        <ActionsContainer>
          <ActionButton> {t("FUNNEL_ACTION_BUTTON_NEXT")}</ActionButton>
        </ActionsContainer>
      </StepContainer>
      <Footer />
    </>
  );
}
